import React from 'react'
import { Element, Heading } from 'react-bulma-components'
import { Field, useFormikContext } from 'formik'
import { formatDuration } from '../../../../utils/date'
import TimeInputField from '../../../form/fields/time-input'
import RadioField from '../../../form/fields/radio'
import { CounterFormSectionProps, CounterFormValues } from './types'

/**
 * Component that displays counter information and allows editing
 */
const CounterFormSection: React.FC<CounterFormSectionProps> = ({
  counterId,
  simulatedData,
  accountedDuration,
  workedDuration,
}) => {
  const { values } = useFormikContext<CounterFormValues>()

  const calculatedHours = simulatedData?.ruleBasedIncrement || 0

  // Calculs pour l'affichage selon le mode
  const getCalculatedToCounterMs = () => {
    switch (values.counters[counterId]?.mode) {
      case 'toCounter':
        return calculatedHours
      case 'toWeek':
        return 0
      case 'split':
        return values.counters[counterId]?.splitToCounter || 0
      default:
        return 0
    }
  }

  const getCalculatedToWeekMs = () => {
    switch (values.counters[counterId]?.mode) {
      case 'toCounter':
        return 0
      case 'toWeek':
        return calculatedHours
      case 'split':
        return values.counters[counterId]?.splitToWeek || 0
      default:
        return 0
    }
  }

  const calculatedToCounterMs = getCalculatedToCounterMs()
  const calculatedToWeekMs = getCalculatedToWeekMs()

  // Validation de la répartition partielle
  const isSplitValid =
    values.counters[counterId]?.mode !== 'split' ||
    Math.abs(
      calculatedToCounterMs + calculatedToWeekMs - (simulatedData?.ruleBasedIncrement || 0),
    ) < 1000 // Tolérance de 1 seconde

  const hasRuleBasedIncrement = React.useMemo(
    () => simulatedData?.ruleBasedIncrement,
    [simulatedData],
  )

  // Use the counter template name if available, otherwise fallback to the ID
  const counterDisplayName = simulatedData?.workerCounter?.template?.name || counterId

  if (!simulatedData?.counter.affectsPeriodSummaryAccounted) {
    return (
      <Element
        p={4}
        backgroundColor="light"
        style={{ borderRadius: '4px', border: '1px solid #dee2e6' }}
      >
        <Heading size={5} mb={3}>
          Compteur {counterDisplayName}
          <Element renderAs="small" textColor="grey" ml={1}>
            - {formatDuration(simulatedData?.ruleBasedIncrement || 0)} calculées pour cette semaine
          </Element>
        </Heading>
        Le compteur {counterDisplayName} n'affecte pas les heures comptabilisées de la semaine.{' '}
        <br />
        Les heures sont donc automatiquement ajoutées au compteur.
      </Element>
    )
  }

  const adjustedValue = simulatedData?.counter.adjustedValue || 0
  const displayAdjustedValue =
    adjustedValue > 0
      ? `- ${formatDuration(adjustedValue)}`
      : `+ ${formatDuration(adjustedValue * -1)}`

  return (
    <Element
      p={4}
      backgroundColor="light"
      style={{ borderRadius: '4px', border: '1px solid #dee2e6' }}
    >
      <Heading size={5} mb={3}>
        Compteur {counterDisplayName}
        <Element renderAs="small" textColor="grey" ml={1}>
          - {formatDuration(simulatedData?.ruleBasedIncrement || 0)} calculées pour cette semaine
        </Element>
      </Heading>

      {/* Mode de gestion des heures calculées */}
      <Element mb={4}>
        <Field
          name={`counters.${counterId}.mode`}
          component={RadioField}
          options={[
            {
              value: 'toCounter',
              label: (
                <Element renderAs={!hasRuleBasedIncrement ? 's' : 'span'}>
                  Ajouter les {formatDuration(simulatedData?.ruleBasedIncrement || 0)} au compteur
                </Element>
              ),
              disabled: !hasRuleBasedIncrement,
            },
            {
              value: 'toWeek',
              label: `Compter les ${formatDuration(simulatedData?.ruleBasedIncrement || 0)} dans la semaine`,
              description: (
                <Element renderAs="small">Reprendre des heures du compteur si nécessaire</Element>
              ),
            },
          ]}
        />

        {/* Récupération d'heures depuis le compteur */}
        {values.counters[counterId]?.mode === 'toWeek' && (
          <Element
            mb={4}
            p={3}
            backgroundColor="light"
            style={{ borderRadius: '4px', border: '1px solid #dee2e6' }}
          >
            <Element>
              <strong>Récupération d'heures depuis le compteur</strong>
            </Element>
            <Element mb={2} textColor="grey" textSize={7}>
              Récupérez des heures du compteur pour les ajouter à la semaine (optionnel)
            </Element>
            <Field name={`counters.${counterId}.recoveryFromCounter`} component={TimeInputField} />
          </Element>
        )}

        <Field
          name={`counters.${counterId}.mode`}
          component={RadioField}
          options={[
            {
              value: 'split',
              label: (
                <Element renderAs={!hasRuleBasedIncrement ? 's' : 'span'}>
                  Divisez les heures entre compteur et semaine
                </Element>
              ),
              disabled: !hasRuleBasedIncrement,
            },
          ]}
        />

        {/* Configuration de la répartition partielle */}
        {values.counters[counterId]?.mode === 'split' && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
            }}
          >
            <Element mb={2}>
              <strong>Configuration de la répartition</strong>
            </Element>

            <Element mb={3}>
              <Field
                label="Heures vers le compteur"
                name={`counters.${counterId}.splitToCounter`}
                component={TimeInputField}
                maxHours={Math.floor(calculatedHours / (60 * 60 * 1000))}
                hoursHelp={`Heures vers le compteur (max: ${Math.floor(calculatedHours / (60 * 60 * 1000))}h)`}
                minutesHelp="Minutes vers le compteur"
              />
            </Element>

            <Element mb={3}>
              <Field
                label="Heures dans la semaine"
                name={`counters.${counterId}.splitToWeek`}
                component={TimeInputField}
                maxHours={Math.floor(calculatedHours / (60 * 60 * 1000))}
                hoursHelp={`Heures dans la semaine (max: ${Math.floor(calculatedHours / (60 * 60 * 1000))}h)`}
                minutesHelp="Minutes dans la semaine"
              />
            </Element>

            {/* Validation de la répartition */}
            <Element>
              <Element renderAs="small">
                <Element renderAs="strong">Total :</Element>{' '}
                {formatDuration(calculatedToCounterMs + calculatedToWeekMs)}
                {!isSplitValid && (
                  <Element renderAs="small" textColor="danger">
                    {' '}
                    - Doit faire exactement {formatDuration(simulatedData?.ruleBasedIncrement || 0)}
                  </Element>
                )}
              </Element>
            </Element>
          </div>
        )}
      </Element>

      {/* Résumé des impacts */}
      {values.counters[counterId]?.mode && (
        <Element
          backgroundColor="success-light"
          mt={1}
          p={3}
          style={{ borderRadius: '4px', border: '1px solid #c8e6c9' }}
        >
          <Heading size={6} textColor="success" mb={2}>
            Résumé des impacts
          </Heading>
          <Element>
            <strong>Total heures comptabilisées dans la semaine : </strong>
            <Element renderAs="span" data-test="week-preview-duration">
              {formatDuration(accountedDuration || 0)}
            </Element>
            <Element renderAs="small" textColor="grey" ml={1} data-test="week-preview-calculation">
              ({formatDuration(workedDuration || 0)} {displayAdjustedValue})
            </Element>
          </Element>
          <Element>
            <strong>Nouveau solde du compteur :</strong>{' '}
            <Element renderAs="span" data-test="counter-preview-duration">
              {formatDuration(simulatedData?.counter.newValue || 0)}
            </Element>
            <Element
              renderAs="small"
              textColor="grey"
              ml={1}
              data-test="counter-preview-calculation"
            >
              ({formatDuration(simulatedData?.counter.previousValue || 0)} +{' '}
              {formatDuration(simulatedData?.counter.adjustedValue || 0)})
            </Element>
          </Element>
        </Element>
      )}
    </Element>
  )
}

export default CounterFormSection
